import PageLayoutModes from '../constants/PageLayoutModes';
import FieldTypes from '../constants/FieldTypes';
import FieldDisplayTypes from '../constants/FieldDisplayTypes';
import FieldTableColumnTypes from '../constants/FieldTableColumnTypes';
import FormStylesOptions from '../constants/FormStylesOptions';
import FieldPresentationModes from '../constants/FieldPresentationModes';
import { compact } from './reactGridLayoutUtils';
import { isEmptyValue } from './validation';
import { resolveAutoHeightEnabled, getAutoHeightPositionChanges } from './autoHeightHelper';

/**
 * Format an array of react-grid-layout layouts from field positions
 *
 * @param {Array} fieldPositions
 * @returns {Array}
 */
export const getLayout = (fieldPositions) => {

  return fieldPositions.map((fieldPosition) => {

    //Convert joyfill position object to supported react-grid-layout object
    return {
      i: fieldPosition._id,
      x: fieldPosition.x,
      y: fieldPosition.y,
      w: fieldPosition.width,
      h: fieldPosition.height
    };

  });

};

/**
 * Calculate the number of PDF Pages (Letter 8 1/2 x 11) will be required to display all page fields.
 * Joyfill page fields are dynamic so they could snap many pages.
 *
 * @param {Array} measuredFieldPositions //field positions that have been properly measured
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @returns {Number} //Number of pages required
 *
 */
export const getPDFPageCountFromPage = (measuredFieldPositions, pageHeight, pageRowHeight) => {

  /**
   * Step 1: Find the field position layout that is last fieldPosition at the bottom of the form.
   */
  let lastFieldPosition = { y: 0, height: 0 };

  measuredFieldPositions.forEach((fieldPosition) => {
    if (fieldPosition.y > lastFieldPosition.y) lastFieldPosition = fieldPosition;
    if (fieldPosition.y === lastFieldPosition.y && fieldPosition.height > lastFieldPosition.height) lastFieldPosition = fieldPosition;
  });

  const bottom = (lastFieldPosition.y * pageRowHeight) + (lastFieldPosition.height * pageRowHeight);

  /**
   * Step 2: Determine how many page breaks this single page actually spans. Dynamic
   * fields can cause a single form page to span multiple page breaks.
   */
  const totalPDFPagesRequired = Math.ceil(bottom / pageHeight);

  return totalPDFPagesRequired;

};

/**
 * Handle the recalculation and update for the x, y properties of field positions.
 *
 * What does recalculation mean?
 *
 * We utilize the "compact" method from the react-grid-layout internals. This allows us
 * to properly recalculate y and height properties of fieldPositions to ensure there are
 * no overlapping or conflicting fieldPositions. For instance, see the example below:
 *
 * Before recalculation:
 * [
 *   { y: 1, height: 10 },
 *   { y: 2, height: 10 },
 * ]
 * after calcualtion
 * [
 *   { y: 1, height: 10 },
 *   { y: 11, height: 10 },
 * ]
 *
 * @param {Array} fieldPositions
 * @param {Number} pageCols
 * @param {String} pageLayout //grid or float
 * @returns {Array} //field positions after their y and height properties have been recalculated
 */
export const recalculateLayoutsForFieldPosition = (fieldPositions, pageCols, pageLayout) => {

  const originalLayouts = getLayout(fieldPositions);
  const nextLayouts = compact(originalLayouts, 'vertical', pageCols, pageLayout === PageLayoutModes.float);

  const layoutLookup = {};
  nextLayouts.forEach((layout) => layoutLookup[layout.i] = layout);

  const nextFieldPositions = [];
  fieldPositions.forEach((fieldPosition) => {

    const layout = layoutLookup[fieldPosition._id];

    nextFieldPositions.push({
      ...fieldPosition,
      x: layout.x,
      y: layout.y,
      height: layout.h,
      width: layout.w
    });

  });

  return nextFieldPositions;

};

/**
 * Get all field position layouts that conflict with the targeted page break index.
 *
 * @param {Array} layouts  //react-grid-layout onLayout handler generated layout objects after render
 * @param {Number} pageBreakTargetIndex //What page break point are we targeting
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @param {Object} pageRowHeight
 * @returns {Array} //field postions that conflict with a page break
 */
export const getFieldPositionsWithConflictsForTargetPageBreak = (fieldPositions, pageBreakTargetIndex, pageHeight, pageRowHeight, pageStyles) => {

  /**
   * Why do we have pageHeightReduction and why is it only for pageBreakTargetIndex 1?
   *
   * Page height reduction explanation: This is because of page styles like borderWidth, margin, padding, etc. change the starting
   * point of the top of fields. For instance we have page heights of 1056 and we have a page style
   * of margin 10 on the page. That means that the top most fieldPositions y coordinate doesn't start at 0,
   * it starts at 10 even though it's saved as fieldPosition.y === 0.
   *
   * Only the pageBreakTargetIndex 1 explanation: The top borderWidth, margin, padding, etc. only push
   * fields down on the very first page. Any field positions that get pushed to a second page don't
   * need to account for those styles because those styles don't apply to any pages other than the first
   * one in the page break list.
   */
  const pageMargin = pageStyles.margin || 0;
  const pagePadding = pageStyles.padding || 0;
  const pageBorderWidth = pageStyles.borderWidth || 0;
  const pageHeightReduction = pageBreakTargetIndex === 1 ? pageMargin + pagePadding + pageBorderWidth : 0;

  const targetPageBreakPointPixels = (pageBreakTargetIndex * pageHeight) - pageHeightReduction;

  const conflictingFieldPositions = [];

  fieldPositions.forEach((fieldPosition) => {

    const fieldPositionTopYPixels = (fieldPosition.y * pageRowHeight);
    const fieldPositionHeightPixels = (fieldPosition.height * pageRowHeight);
    const fieldPositionBottomYPixels = fieldPositionTopYPixels + fieldPositionHeightPixels;

    if (fieldPositionTopYPixels < targetPageBreakPointPixels && fieldPositionBottomYPixels > targetPageBreakPointPixels) {
      conflictingFieldPositions.push(fieldPosition);
    }

  });

  return conflictingFieldPositions;

};

/**
 * Adjust a field position so that it's not in the way of a page break.
 *
 * @param {Object} fieldPosition
 * @param {Number} pageBreakTargetIndex
 * @param {Number} pageHeight
 * @param {Number} pageRowHeight
 * @param {Object} pageStyles //Page padding, margin and border width
 *
 * @returns {Object} //updated field positon
 */
export const adjustFieldPositionForPageBreakConflict = (fieldPosition, pageBreakTargetIndex, pageHeight, pageRowHeight, pageStyles) => {

  /**
   * Field positions that have a height greater than the page height will
   * force a page break through the field on the form.
   */
  if ((fieldPosition.height * pageRowHeight) > pageHeight) return fieldPosition;

  /**
   * IMPORTANT NOTE: Ensure 0 is assigned when undefined. Without this the
   * height property is not calculated properly and generates a NaN value and
   * crashes the SDK.
   */
  const pageMargin = pageStyles.margin || 0;
  const pagePadding = pageStyles.padding || 0;
  const pageBorderWidth = pageStyles.borderWidth || 0;

  const pageHeightReduction = pageBreakTargetIndex === 1 ? pageMargin + pagePadding + pageBorderWidth : 0;

  const targetPageBreakPointPixels = (pageBreakTargetIndex * pageHeight) - pageHeightReduction;

  const fieldPositionTopYPixels = (fieldPosition.y * pageRowHeight);

  const topOverflowPixels = targetPageBreakPointPixels - fieldPositionTopYPixels;
  const topOverflowRows = Math.ceil(topOverflowPixels / pageRowHeight);

  const pageBreakPaddingPixels = 18;
  const pageBreakPaddingRows = Math.ceil(pageBreakPaddingPixels / pageRowHeight);

  //Used to adjust field height
  const nextHeight = fieldPosition.height + topOverflowRows + pageBreakPaddingRows;
  //Used to add white space above the field and push it below the page break line
  const internalTopPadding = (topOverflowRows + pageBreakPaddingRows) * pageRowHeight;

  /**
  * Ignore dynamic fields for now until we can do internal field adjusting in at
  * a future time.
  */
  if (fieldPosition.type === FieldTypes.table || fieldPosition.type === FieldTypes.chart) {
    return fieldPosition;
  }

  return {
    ...fieldPosition,
    height: nextHeight,
    internalTopPadding
  };

};

/**
 * Adjust the field positions to prevent page break issues.
 *
 * @param {Object} page
 * @returns {Array} //adjusted page field positions
 */
export const adjustFieldPostionsForPageBreaks = (page) => {

  const { fieldPositions, rowHeight, cols, height, padding, margin, borderWidth, layout } = page;

  const pageBreakCount = getPDFPageCountFromPage(fieldPositions, height, rowHeight) + 1;

  let adjustedFieldPositions = fieldPositions;
  const previouslyAdjustedFieldPositionLookup = {};

  for (let targetPageBreakIndex = 1; targetPageBreakIndex < pageBreakCount; targetPageBreakIndex++) {

    const conflictingPositions = getFieldPositionsWithConflictsForTargetPageBreak(adjustedFieldPositions, targetPageBreakIndex, height, rowHeight, { padding, margin, borderWidth });

    if (conflictingPositions.length > 0) {

      const resolvedFieldPositionLookup = {};
      conflictingPositions.forEach((fieldPositionConflict) => {

        /**
         * Only adjust fieldPositions that have not been previously adjusted.
         * What would cause a field to be adjusted twice? This would be caused by the fieldPosition
         * conflicting at multiple page break points. Usually only dynamic fields, like tables, grow
         * to intersect at multiple page break points.
         */
        if (!previouslyAdjustedFieldPositionLookup[fieldPositionConflict._id]) {
          previouslyAdjustedFieldPositionLookup[fieldPositionConflict._id] = true;
          resolvedFieldPositionLookup[fieldPositionConflict._id] = adjustFieldPositionForPageBreakConflict(fieldPositionConflict, targetPageBreakIndex, height, rowHeight, { padding, margin, borderWidth });
        }
      });

      adjustedFieldPositions = adjustedFieldPositions.map((fieldPosition) => {
        return resolvedFieldPositionLookup[fieldPosition._id] || fieldPosition;
      });

      adjustedFieldPositions = recalculateLayoutsForFieldPosition(adjustedFieldPositions, cols, layout);

    }

  }

  return adjustedFieldPositions;

};

/**
 * Resolve the height a measured field position should be given in the export.
 *
 * Measuring is allowed to make a box taller. For text and multiline fields it is not allowed to
 * make one shorter, so the height the designer drew acts as a floor.
 *
 * Why the floor? Both fields report the height their content needs while they are being
 * measured, which is what lets a long value grow the box. A short value in a deliberately tall
 * box therefore measures shorter than it was drawn, and without a floor the export would
 * quietly shrink the designer's box down to fit a couple of words. Multiline fields did exactly
 * that up to rc26: a 96px box holding a one line answer printed 40px tall, and the grid packed
 * the shrunken boxes upward, away from the labels drawn beside them. The builder never resizes
 * a box, so this floor is what makes the two agree.
 *
 * Every other field type keeps the previous behaviour of taking the measurement as is.
 *
 * Both values are in grid rows, the unit ExportFormPage converts pixel measurements into.
 *
 * @param {String} type //Field type
 * @param {Number} drawnHeight //The height saved on the field position
 * @param {Number} measuredHeight //The height the field reported
 * @returns {Number}
 */
export const resolveMeasuredFieldHeight = (type, drawnHeight, measuredHeight) => {

  if (type !== FieldTypes.text && type !== FieldTypes.textarea) return measuredHeight;

  //Guards against a missing or malformed drawn height rather than returning NaN
  if (typeof drawnHeight !== 'number') return measuredHeight;

  return Math.max(measuredHeight, drawnHeight);

};

/**
 * Turn an auto-height export measurement into the height (and, for a fillable PDF textarea,
 * the line pitch) the field should be drawn with.
 *
 * This is PDFPage.handleFieldAutoHeightMeasured without the parts that only matter on a live
 * screen: there is no stale check because the measuring pass drew the page once from the
 * document, and no viewport scaling because it drew it at page.width, so a measured pixel is a
 * design pixel. Everything else, the safety pixel, two decimal rounding and the pitch rescale,
 * is the builder's own, so an exported box is the box a blur would have produced.
 *
 * Grow only, by `shrink: false`: the builder's calculator can shrink a box after an edit, the
 * exporter never does. With shrink off the calculator runs the arithmetic it always ran, and the
 * measurement it is fed is the one it was always fed (autoHeightOverflowPx is the field's
 * legacyOverflowPx, scrollHeight less clientHeight), so an exported box lands on the same pixel
 * as before. Zero overflow leaves the field as drawn. Nothing here is persisted; it lives on the
 * export copy of the field position.
 *
 * @param {Object} params
 * @param {Object} params.fieldPosition //The export copy of the field position, file styles applied
 * @param {Object} params.measurement //{ autoHeightOverflowPx, autoHeightBoxPx } from ExportFormPage
 * @param {Number} params.rowHeight //The page's row height, 1 on float pages made by the app
 * @param {String} params.presentation //Page presentation, decides whether ruled lines exist
 * @returns {Object|null}
 */
export const resolveAutoHeightExportChanges = ({ fieldPosition, measurement, rowHeight, presentation }) => {

  //Only a textarea on a fillable PDF section draws ruled lines whose pitch must survive the growth
  const keepLinePitch = fieldPosition.type === FieldTypes.textarea
    && presentation === FieldPresentationModes.transparent;

  const result = getAutoHeightPositionChanges({
    fieldPosition,
    measurement: {
      overflowPx: measurement.autoHeightOverflowPx,
      boxHeightPx: measurement.autoHeightBoxPx,
      empty: false,
      heightAtMeasure: fieldPosition.height,
    },
    snapToRows: false,
    rowHeight: rowHeight === undefined ? 1 : rowHeight,
    percentageDifference: 0,
    keepLinePitch,
    shrink: false,
  });

  return result ? result.changes : null;

};

/**
 * Get list of fields that should be measured by the PDF Export Page
 *
 * @param {Array} fields
 * @returns {Array};
 */
export const getMeasurableFields = (fields, licenseValidation) => {
  const result = fields.filter((field) => {

    const { type, displayType, columnType, multi } = field;

    const isCollectionFieldEnabled = licenseValidation?.decodedData?.collectionField;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.original) return true;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.inputGroup) return true;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.rte) return true;

    if (type === FieldTypes.table && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.table && displayType === FieldDisplayTypes.inputGroup) return true;
    if (type === FieldTypes.table && displayType === FieldDisplayTypes.rte) return true;
    if (type === FieldTypes.table && columnType === FieldTableColumnTypes.image) return true;

    if (type === FieldTypes.chart && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.rte && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.pricingTable && displayType === FieldDisplayTypes.original) return true;

    if (type === FieldTypes.multiSelect && (displayType === FieldDisplayTypes.original || displayType === FieldDisplayTypes.horizontal)) return true;

    //The following fields should only be measured if they have a populated value
    if (type === FieldTypes.block && displayType === FieldDisplayTypes.original) return !isEmptyValue(field.value, FieldTypes.block);
    if (type === FieldTypes.image && displayType === FieldDisplayTypes.original && multi) return !isEmptyValue(field.value, FieldTypes.image);
    if (type === FieldTypes.file && displayType === FieldDisplayTypes.original && multi) return !isEmptyValue(field.value, FieldTypes.file);
    /**
     * Auto-shrink fields must never be measured. Measuring exists to grow the field to fit
     * its text, and auto-shrink does the opposite, so if both ran the field would grow and
     * maxLines would never apply. Returning false leaves the field at the height the designer
     * drew, which is the whole point of the feature.
     */
    if (type === FieldTypes.textarea && field.autoShrink === true) return false;

    if (type === FieldTypes.textarea && displayType === FieldDisplayTypes.original) return !isEmptyValue(field.value, FieldTypes.textarea);

    return false;

  });
  return result;
};

/**
 * Layout types that represent the leading chrome (field title, nested schema
 * title, or column header) that always renders before the first data row of an
 * original-display table/collection field.
 */
const LEADING_CHROME_LAYOUT_TYPES = ['fieldTitle', 'title', 'header'];

/**
 * Reconcile the leading-chrome height for original-display table/collection
 * measurements so that sum(layouts[].height) === the field's measured height.
 *
 * Why is this needed?
 *
 * TableFieldv2/CollectionField report a measured `height` from
 * containerRef.getBoundingClientRect() plus a `layouts[]` list of individual
 * split-unit heights (title, header, rows). The container height includes
 * leading chrome (title wrapper padding, gaps, sub-pixel rounding) that the
 * summed layout heights can slightly undercount. The PDF renderer's
 * splitDynamicVerticalField trusts `layouts[]` for dynamic vertical splitting,
 * so an undercounted sum makes the field occupy less vertical space in
 * pagination math than it does in the real DOM, causing the next field to
 * overlap it (NO-2223).
 *
 * The fix folds any residual (measuredHeight - sum(layouts)) into the first
 * leading-chrome layout entry so the renderer's split math matches the DOM.
 *
 * This is intentionally narrow:
 * - table/collection field types only
 * - original display type only
 * - no-op unless the first layout is leading chrome (never row-first layouts
 *   such as input-group, and never image-field layouts)
 * - no-op when there is no positive residual
 *
 * @param {Object} params
 * @param {Array} params.layouts - The layouts array from the field measurement
 * @param {Number} params.measuredHeight - The field's measured container height
 * @param {String} params.fieldType - The field type (table/collection)
 * @param {String} params.displayType - The field display type
 * @returns {Array} - Reconciled layouts array (unchanged reference when no-op)
 */
export const reconcileTableCollectionLeadingChrome = ({ layouts, measuredHeight, fieldType, displayType }) => {

  if (!layouts || layouts.length === 0) return layouts;

  //Scope: table/collection fields only. Image and other dynamic fields are untouched.
  if (fieldType !== FieldTypes.table && fieldType !== FieldTypes.collection) return layouts;

  //Scope: original display only. Input-group/rte row-first layouts are untouched.
  if (displayType !== FieldDisplayTypes.original && displayType !== FieldDisplayTypes.inputGroup) return layouts;

  //Only reconcile when the first split unit is leading chrome, never a real row.
  if (!LEADING_CHROME_LAYOUT_TYPES.includes(layouts[0].type)) return layouts;

  const layoutsSum = layouts.reduce((sum, l) => sum + (l.height || 0), 0);
  const residual = measuredHeight - layoutsSum;

  if (residual > 0 && layoutsSum > 0) {
    return layouts.map((l, i) => (i === 0 ? { ...l, height: l.height + residual } : l));
  }

  return layouts;
};

/**
 * Get list of fields that should be measured by the PDF Exporter.
 *
 * This is separate from getMeasurableFields (used by the editor's autoResizeFields)
 * so that changes to autoResizeFields cannot affect PDF export behavior.
 *
 * multiSelect/ block is excluded because its options are fixed template structure
 * with designer-set heights that should be preserved in PDF export.
 *
 * @param {Array} fields
 * @param {Object} licenseValidation
 * @param {String} layout //Page layout. Header and footer pass none and take the grid rules.
 * @returns {Array}
 */
export const getExporterMeasurableFields = (fields, licenseValidation, layout) => {

  /**
   * A float (PDF) page. Every box on it was placed by hand over an image, so nothing is
   * measured unless the designer switched auto-height on for it and it has something in it.
   *
   * This rule and the field-level flag (isExporterAutoHeight in TextField and TextareaField,
   * together with their populated-value check) must describe the same set of fields. A field
   * admitted here that never reports leaves the export on "Field Measuring In Progress"
   * forever; a field that reports without being admitted is simply ignored.
   */
  if (layout === PageLayoutModes.float) {
    return fields.filter((field) => (
      field.displayType === FieldDisplayTypes.original
      && resolveAutoHeightEnabled({ type: field.type, autoHeight: field.autoHeight })
      && !isEmptyValue(field.value, field.type)
    ));
  }

  return fields.filter((field) => {

    const { type, displayType, columnType, multi, textOverflow } = field;

    const isCollectionFieldEnabled = licenseValidation?.decodedData?.collectionField;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.original) return true;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.inputGroup) return true;
    if (isCollectionFieldEnabled && type === FieldTypes.collection && displayType === FieldDisplayTypes.rte) return true;

    if (type === FieldTypes.table && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.table && displayType === FieldDisplayTypes.inputGroup) return true;
    if (type === FieldTypes.table && displayType === FieldDisplayTypes.rte) return true;
    if (type === FieldTypes.table && columnType === FieldTableColumnTypes.image) return true;

    if (type === FieldTypes.chart && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.rte && displayType === FieldDisplayTypes.original) return true;
    if (type === FieldTypes.pricingTable && displayType === FieldDisplayTypes.original) return true;

    //The following fields should only be measured if they have a populated value
    if (type === FieldTypes.image && displayType === FieldDisplayTypes.original && multi) return !isEmptyValue(field.value, FieldTypes.image);
    if (type === FieldTypes.file && displayType === FieldDisplayTypes.original && multi) return !isEmptyValue(field.value, FieldTypes.file);
    if (type === FieldTypes.textarea && displayType === FieldDisplayTypes.original) return !isEmptyValue(field.value, FieldTypes.textarea);

    /**
     * Single line text fields grow to fit their wrapped value, the same way multiline
     * fields do above.
     *
     * The wrapping condition mirrors TextField's own isValueWrapping exactly. Auto-shrink and
     * auto-height both force wrapping on regardless of textOverflow (they do on screen, and the
     * export must draw the node it measures), which is why they are tested first. A field left
     * on ellipsis with neither is drawn as a real one line <input> that cannot wrap, so there
     * is no extra height to find and the designer asked for truncation anyway.
     *
     * autoHeight goes through resolveAutoHeightEnabled, the resolver Field.js uses, so a
     * malformed legacy value can never admit a field that TextField will not measure.
     *
     * If this rule and TextField ever disagreed, a field would wrap on screen but be refused
     * a measurement, and its text would spill past the border with nothing to catch it.
     */
    if (type === FieldTypes.text && displayType === FieldDisplayTypes.original
      && (field.autoShrink === true
        || resolveAutoHeightEnabled({ type, autoHeight: field.autoHeight })
        || textOverflow !== FormStylesOptions.textOverflowEllipsis)) {
      return !isEmptyValue(field.value, FieldTypes.text);
    }

    return false;

  });
};
